/**
 * "Autopilot" landing page for the "Für Makler" section.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { keyframes } from '@emotion/react';
import {
  AspectRatio,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
} from '@hausgold/designsystem';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import SEO from '../../components/SEO';
import Layout from '../../components/Layout';
import Link from '../../components/Link';

import ExitIntent from '../../components/ExitIntent';
import ExitIntentModal from '../../components/ExitIntentModal';

// --- Shared design tokens (mirrors the mock's CSS custom properties) --------
const INK = 'gray.600'; // --ink  #2E2D38
const MUTED = 'gray.400'; // --gray #6B6A78
const LINE = 'rgba(46,45,56,.10)'; // --line
const SHADOW = '0 24px 60px rgba(46,45,56,.10)'; // --shadow
const SHADOW_SM = '0 10px 30px rgba(46,45,56,.07)'; // --shadow-sm
const CTA_URL = '/koop-anfrage';

// --- Keyframes --------------------------------------------------------------
const marquee = keyframes`from { transform: translateX(0); } to { transform: translateX(-50%); }`;
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(244,99,100,.5); }
  70% { box-shadow: 0 0 0 10px rgba(244,99,100,0); }
  100% { box-shadow: 0 0 0 0 rgba(244,99,100,0); }
`;
const ripple = keyframes`
  0% { transform: scale(1); opacity: .7; }
  100% { transform: scale(1.9); opacity: 0; }
`;
const wave = keyframes`0%, 100% { transform: scaleY(.4); } 50% { transform: scaleY(1); }`;

// --- Small building blocks --------------------------------------------------

// Centered max-width container (mock's `.wrap`).
const Wrap = ({ children, ...props }) => (
  <Box maxW="1160px" mx="auto" px={{ base: '22px', md: '32px' }} {...props}>
    {children}
  </Box>
);

// Uppercase, letter-spaced eyebrow label (mock's `.kicker`).
const Kicker = ({ children, color = 'blue.400' }) => (
  <Text
    as="span"
    display="block"
    fontSize="12px"
    letterSpacing="0.16em"
    textTransform="uppercase"
    fontWeight={700}
    color={color}
    mb="14px"
  >
    {children}
  </Text>
);

// "Teil des Autopiloten" marker with a short leading rule (mock's `.belongs`).
const Belongs = ({ children }) => (
  <Flex align="center" gap="8px" mb="16px">
    <Box w="22px" h="1px" bg="blue.400" flex="none" />
    <Text
      as="span"
      fontSize="11.5px"
      letterSpacing="0.12em"
      textTransform="uppercase"
      fontWeight={700}
      color="blue.400"
      mb="0"
    >
      {children}
    </Text>
  </Flex>
);

// Checklist of features (mock's `.feats`). Each item: [lead, rest].
const FeatList = ({ items, mb = '22px' }) => (
  <Flex
    as="ul"
    direction="column"
    gap="14px"
    listStyleType="none"
    m="0"
    mb={mb}
    p="0"
  >
    {items.map(([lead, rest]) => (
      <Flex as="li" key={lead} align="flex-start" gap="12px">
        <Flex
          flex="none"
          align="center"
          justify="center"
          w="18px"
          h="18px"
          mt="3px"
          borderRadius="5px"
          bg="white"
          color="blue.400"
          fontSize="12px"
          fontWeight={700}
          boxShadow="0 1px 3px rgba(0,0,0,.06)"
        >
          ✓
        </Flex>
        <Text as="span" fontSize="16px" color={MUTED} lineHeight="1.5" mb="0">
          <Text as="b" color={INK} fontWeight={700}>
            {lead}
          </Text>{' '}
          {rest}
        </Text>
      </Flex>
    ))}
  </Flex>
);

/*
 * Primary call-to-action button, defaults to the koop-anfrage flow.
 * `source` identifies which button fired the tracking; it is forwarded to
 * `Link` as the `eventLabel`, so the built-in link tracking carries it and no
 * additional tracking call is needed.
 */
const Cta = ({
  children,
  colorScheme = 'red',
  variant,
  large,
  to = CTA_URL,
  source,
  ...rest
}) => (
  <Button
    as={Link}
    to={to}
    eventLabel={source || (typeof children === 'string' ? children : 'CTA')}
    colorScheme={colorScheme}
    variant={variant}
    fontWeight={700}
    borderRadius="10px"
    height="auto"
    fontSize={large ? '17px' : '16px'}
    px={large ? '34px' : '24px'}
    py={large ? '17px' : '13px'}
    {...rest}
  >
    {children}
  </Button>
);

// --- Content data -----------------------------------------------------------
const TESTIMONIALS = [
  {
    photo: '/autopilot/testimonials/christine-fuenner.png',
    name: 'Christine Fünner',
    meta: 'Deutsche Bank Immobilien GmbH · Schwäbisch Gmünd',
    quote: 'HAUSGOLD hat aktuell die besten Kundenkontakte am Markt.',
    link: '/makler-erfahrungen/erfahrungen_fuenner',
  },
  {
    photo: '/autopilot/testimonials/lars-barthelmes.png',
    name: 'Lars Barthelmes',
    meta: 'Herkules Immobilien · Kassel',
    quote: 'Durch HAUSGOLD konnte ich meine Erfolgsquote steigern.',
    link: '/makler-erfahrungen/erfahrungen_barthelmes',
  },
  {
    photo: '/autopilot/testimonials/sebastian-schaedla.png',
    name: 'Sebastian Schädla',
    meta: 'MT-IMMOBILIEN · Hannover',
    quote: 'Hohe Lead-Qualität und ausgezeichnete Zusammenarbeit.',
    link: '/makler-erfahrungen/erfahrungen_schaedla',
  },
];

const SECURITY = [
  {
    icon: '/autopilot/icons/server-eu.png',
    title: 'Server in der EU',
    text: 'Speicherung ausschließlich in der EU.',
  },
  {
    icon: '/autopilot/icons/lock-outline.png',
    title: 'SSL-verschlüsselt',
    text: 'Sichere Übertragung.',
  },
  {
    icon: '/autopilot/icons/chip-ai-check-outline.png',
    title: 'Autopilot: AI-Act- & DSGVO-konform',
    text: 'In Ihrem Auftrag, Sie bleiben Verantwortlicher.',
  },
  {
    icon: '/autopilot/icons/account-multiple-check-outline.png',
    title: 'Börse: DSGVO-konform',
    text: 'Kontaktvermittlung rechtssicher.',
  },
];

const MAKLER_LOGOS = [
  { src: '/autopilot/logos/remax.png', alt: 'RE/MAX' },
  { src: '/autopilot/logos/ev.png', alt: 'Engel & Völkers' },
  { src: '/autopilot/logos/iad.png', alt: 'iad' },
  { src: '/autopilot/logos/planethome.png', alt: 'PlanetHome' },
  { src: '/autopilot/logos/db-immobilien.jpg', alt: 'DB Immobilien' },
  { src: '/autopilot/logos/wuestenrot.png', alt: 'Wüstenrot' },
  { src: '/autopilot/logos/lbs.svg', alt: 'LBS' },
  { src: '/autopilot/logos/garant.webp', alt: 'Garant Immobilien' },
  { src: '/autopilot/logos/kensington.webp', alt: 'Kensington' },
  { src: '/autopilot/logos/c21.png', alt: 'Century 21' },
  { src: '/autopilot/logos/falc.jpg', alt: 'FALC Immobilien' },
];

const TIMELINE = [
  {
    step: 'Schritt 1',
    title: 'Anmelden',
    text: 'In wenigen Minuten ausgefüllt.',
  },
  {
    step: 'Schritt 2',
    title: 'Freischalten',
    text: 'Meist in unter einer Stunde.',
  },
  {
    step: 'Schritt 3',
    title: 'Läuft',
    text: 'Erste Objekte und die ersten Termine.',
  },
];

const FAQ = [
  {
    group: 'Objektbörse',
    items: [
      {
        q: 'Sind das echte, verkaufsbereite Eigentümer?',
        a: 'Jeder Kontakt wird vor der Übergabe telefonisch geprüft. Ein Teil der Objekte trägt zusätzlich eine Qualitätsgarantie. Das ist immer klar gekennzeichnet, und Sie sehen es zusammen mit Lage, Objekttyp und Verkaufsabsicht schon vor dem Kauf. So entscheiden Sie selbst, welche Kontakte Sie übernehmen.',
      },
      {
        q: 'Gemeinschaftsgeschäft oder Festpreis — was ist der Unterschied?',
        a: 'Beim Gemeinschaftsgeschäft zahlen Sie vorab nur eine kleine Gebühr und beteiligen uns dafür an der Provision, fällig erst nach dem Verkauf. Beim Festpreis zahlen Sie einen festen Betrag je Kontakt, die volle Provision aus dem Verkauf bleibt bei Ihnen.',
      },
    ],
  },
  {
    group: 'Autopilot',
    items: [
      {
        q: 'Wie tritt der Autopilot beim Eigentümer auf?',
        a: 'Der Autopilot meldet sich transparent als digitaler Assistent in Ihrem Auftrag und führt freundliche, sachliche Gespräche. Er arbeitet AI-Act- und DSGVO-konform, die Daten liegen in der EU, Sie bleiben Verantwortlicher.',
      },
      {
        q: 'Ich rufe lieber selbst an, verliere ich die persönliche Bindung?',
        a: 'Nein. Der Autopilot erreicht die Eigentümer, führt die ersten Gespräche und vereinbart, wo es passt, auch schon den Termin. Sie steigen dort ein, wo Ihre persönliche Bindung zählt, im Gespräch mit dem Verkäufer. Und wenn Sie von Anfang an selbst sprechen möchten, übernehmen Sie jeden Kontakt jederzeit.',
      },
    ],
  },
  {
    group: 'Allgemein',
    items: [
      {
        q: 'Was kostet mich das?',
        a: 'Objekte aus der Börse zahlen Sie je Kontakt, per Festpreis oder Gemeinschaftsgeschäft. Keine Fixkosten, keine Mindestabnahme. Der Autopilot ist in der Testphase kostenlos, danach ein fester Monatspreis nach Umfang.',
      },
      {
        q: 'Muss ich Börse und Autopilot zusammen nutzen?',
        a: 'Nein. Beide funktionieren einzeln: Kontakte kaufen, den Autopiloten für Ihre eigenen Kontakte, oder beides zusammen.',
      },
    ],
  },
];

// Waveform bars for the "Autopilot ruft an" card: [height(px), delay(s)].
const WAVE_BARS = [
  [10, 0],
  [22, 0.1],
  [14, 0.2],
  [30, 0.3],
  [18, 0.4],
  [26, 0.5],
  [12, 0.6],
  [28, 0.7],
  [16, 0.8],
  [24, 0.9],
  [11, 0.35],
  [20, 0.55],
];

// --- Widget cards -----------------------------------------------------------

// Objektbörse: a single "geprüftes Objekt" preview card.
const ObjectCard = () => (
  <Box bg="white" borderRadius="16px" boxShadow={SHADOW} overflow="hidden">
    <Box bg="blue.400" color="white" px="16px" pt="10px" pb="11px">
      <Text
        as="span"
        display="block"
        fontSize="10px"
        letterSpacing="0.1em"
        textTransform="uppercase"
        opacity={0.85}
        mb="0"
      >
        Aus fast allen Quellen am Markt
      </Text>
      <Text
        as="span"
        display="block"
        fontWeight={700}
        fontSize="13.5px"
        mt="3px"
        mb="0"
      >
        10.000+ geprüfte Objekte im Monat, eines davon:
      </Text>
    </Box>
    <Box p="20px">
      <Flex gap="6px" mb="12px" wrap="wrap">
        <Box
          borderRadius="5px"
          px="9px"
          py="4px"
          fontSize="11.5px"
          fontWeight={700}
          bg="red.400"
          color="white"
        >
          Gold-Objekt
        </Box>
        <Box
          borderRadius="5px"
          px="9px"
          py="4px"
          fontSize="11.5px"
          fontWeight={700}
          bg="blue.400"
          color="white"
        >
          Neu
        </Box>
        <Box
          borderRadius="5px"
          px="9px"
          py="4px"
          fontSize="11.5px"
          fontWeight={700}
          bg="#F4F5F7"
          color={INK}
        >
          geprüft
        </Box>
      </Flex>
      <Heading as="h4" fontSize="19px" color={INK} mb="0">
        Einfamilienhaus
      </Heading>
      <Text fontSize="14px" color={MUTED} mb="12px">
        22850 Norderstedt-Garstedt
      </Text>
      <Box
        bg="#F7F8FA"
        border="1px dashed"
        borderColor={LINE}
        borderRadius="9px"
        p="13px"
        mb="14px"
      >
        {[
          ['Eigentümerin', 'M. ██████'],
          ['Telefon', '+49 40 ██ ██ ██'],
          ['Wertspanne', '480.000 bis 540.000 €'],
          ['Verkaufsabsicht', 'innerhalb 3 Monate'],
        ].map(([label, value]) => (
          <Flex
            key={label}
            justify="space-between"
            fontSize="14px"
            mb="5px"
            gap="10px"
          >
            <Text as="span" color={MUTED} mb="0">
              {label}
            </Text>
            <Text as="b" color={INK} fontWeight={500} textAlign="right" mb="0">
              {value}
            </Text>
          </Flex>
        ))}
        <Text fontSize="12.5px" color={MUTED} display="block" mt="8px" mb="0">
          Name, Adresse und Telefon erhalten Sie nach der Übernahme.
        </Text>
      </Box>
      <Box
        as="span"
        display="flex"
        justifyContent="center"
        w="100%"
        bg={INK}
        color="white"
        fontWeight={700}
        fontSize="16px"
        borderRadius="10px"
        py="13px"
      >
        Kontakt annehmen
      </Box>
    </Box>
  </Box>
);

// Autopilot: dark "device" card visualising an ongoing call.
const CallCard = () => (
  <Box bg={INK} borderRadius="16px" p="20px" boxShadow={SHADOW}>
    <Flex
      align="center"
      gap="9px"
      pb="14px"
      borderBottom="1px solid"
      borderColor="whiteAlpha.300"
    >
      <Box
        w="8px"
        h="8px"
        borderRadius="50%"
        bg="red.400"
        flex="none"
        animation={`${pulse} 2s infinite`}
      />
      <Text as="b" color="white" fontSize="15px" mb="0">
        Autopilot ruft an
      </Text>
      <Text as="span" ml="auto" fontSize="11px" color="whiteAlpha.600" mb="0">
        live · 22850
      </Text>
    </Flex>

    <Flex direction="column" align="center" gap="15px" pt="24px" pb="18px">
      <Flex
        position="relative"
        w="56px"
        h="56px"
        borderRadius="50%"
        bg="rgba(16,152,243,.18)"
        border="1px solid rgba(16,152,243,.45)"
        align="center"
        justify="center"
        sx={{
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '1px solid rgba(16,152,243,.4)',
            animation: `${ripple} 2.4s infinite`,
          },
          '&::after': { animationDelay: '1.2s' },
        }}
      >
        <Box as="span" fontSize="23px">
          📞
        </Box>
      </Flex>
      <Flex align="center" gap="4px" h="32px">
        {WAVE_BARS.map(([h, delay], i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            w="4px"
            h={`${h}px`}
            borderRadius="2px"
            bg={i % 2 === 0 ? 'blue.400' : 'red.400'}
            animation={`${wave} 1s ease-in-out infinite`}
            sx={{ animationDelay: `${delay}s` }}
          />
        ))}
      </Flex>
      <Text fontSize="13px" color="whiteAlpha.700" mb="0">
        Eigentümerin erreicht · 11:24 Uhr
      </Text>
    </Flex>

    <Flex
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="whiteAlpha.300"
      mb="15px"
    >
      {[
        ['3.', 'Versuch, richtig getimt'],
        ['Telefon', 'bester Kanal'],
        ['hoch', 'Abschlusschance'],
      ].map(([big, small], i) => (
        <Box
          key={small}
          flex="1"
          textAlign="center"
          py="12px"
          borderRight={i < 2 ? '1px solid' : undefined}
          borderColor="whiteAlpha.300"
        >
          <Text
            as="b"
            display="block"
            fontSize="18px"
            color="white"
            lineHeight="1"
            mb="0"
          >
            {big}
          </Text>
          <Text
            as="span"
            display="block"
            fontSize="11px"
            color="whiteAlpha.600"
            mt="5px"
            mb="0"
          >
            {small}
          </Text>
        </Box>
      ))}
    </Flex>

    <Flex align="center" gap="8px" mb="11px">
      <Flex
        w="19px"
        h="19px"
        borderRadius="5px"
        bg="red.400"
        align="center"
        justify="center"
        color="white"
        fontSize="11px"
        fontWeight={700}
        flex="none"
      >
        ✓
      </Flex>
      <Text as="b" color="white" fontSize="15px" mb="0">
        Termin gemacht, an Sie übergeben
      </Text>
    </Flex>
    <Box
      bg="whiteAlpha.100"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="9px"
      p="12px"
    >
      {[
        ['Bewertung', 'Do, 15:00 Uhr, vor Ort'],
        ['Absicht', 'Verkauf im Frühjahr'],
        ['Notiz', 'Wertgutachten liegt vor'],
      ].map(([k, v]) => (
        <Flex key={k} gap="10px" fontSize="14px" mb="5px">
          <Text
            as="span"
            color="whiteAlpha.600"
            w="7em"
            flex="none"
            fontSize="11px"
            pt="2px"
            mb="0"
          >
            {k}
          </Text>
          <Text as="b" color="white" fontWeight={400} mb="0">
            {v}
          </Text>
        </Flex>
      ))}
    </Box>
  </Box>
);

// "So läuft es": live cockpit list of automated / manual tasks.
const CockpitCard = () => {
  const rows = [
    {
      av: 'MH',
      title: 'Termin bestätigen',
      sub: 'Do 15:00 · Bewertung vor Ort',
      actor: 'Sie',
      mine: true,
    },
    {
      av: 'TK',
      title: 'Ruft erneut an',
      sub: 'Rückruf für morgen früh geplant',
      actor: 'Autopilot',
    },
    {
      av: 'SB',
      title: 'E-Mail nachgefasst',
      sub: 'wartet auf Antwort',
      actor: 'Autopilot',
    },
    {
      av: 'RP',
      title: 'SMS versendet',
      sub: 'Erinnerung an Rückruf',
      actor: 'Autopilot',
    },
  ];
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor={LINE}
      borderRadius="16px"
      p="20px"
      boxShadow={SHADOW}
    >
      <Flex
        fontSize="12px"
        letterSpacing="0.12em"
        textTransform="uppercase"
        color={MUTED}
        pb="14px"
        borderBottom="1px solid"
        borderColor={LINE}
      >
        <Text as="span" mb="0">
          Heute · Ihr Autopilot
        </Text>
        <Text as="span" ml="auto" color="blue.400" mb="0">
          läuft
        </Text>
      </Flex>
      {rows.map((row) => (
        <Flex
          key={row.av}
          align="center"
          gap="12px"
          py="14px"
          borderBottom="1px solid"
          borderColor={LINE}
        >
          <Flex
            w="34px"
            h="34px"
            borderRadius="8px"
            bg="blue.200"
            align="center"
            justify="center"
            fontSize="13px"
            flex="none"
            color="blue.400"
            fontWeight={700}
          >
            {row.av}
          </Flex>
          <Box flex="1" minW="0">
            <Text as="b" display="block" fontSize="15px" color={INK} mb="0">
              {row.title}
            </Text>
            <Text as="span" fontSize="12.5px" color={MUTED} mb="0">
              {row.sub}
            </Text>
          </Box>
          <Text
            as="span"
            flex="none"
            fontSize="11px"
            color={row.mine ? 'blue.400' : MUTED}
            bg={row.mine ? 'blue.200' : '#F2F4F7'}
            px="9px"
            py="5px"
            borderRadius="6px"
            mb="0"
          >
            {row.actor}
          </Text>
        </Flex>
      ))}
      <Text pt="13px" fontSize="13px" color={MUTED} mb="0">
        12 laufen automatisch · 1 wartet auf Sie
      </Text>
    </Box>
  );
};

// --- Page -------------------------------------------------------------------
const Autopilot = ({ data, location }) => {
  const page = get(data, 'contentfulPage');
  const site = get(data, 'site');
  const exitIntent = get(page, 'exitIntent');

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const removeExitIntent = ExitIntent({
      threshold: 10,
      eventThrottle: 100,
      onExitIntent: () => {
        setShowPopup(true);
      },
    });
    return () => {
      removeExitIntent();
    };
  }, []);

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Startseite',
        url: `${site.siteMetadata.siteUrl}/`,
      },
      {
        title: page.title,
        url: `${site.siteMetadata.siteUrl}/fuer-makler/${page.slug}/`,
      },
    ],
    [page.slug, page.title, site.siteMetadata.siteUrl]
  );

  return (
    <Layout
      location={location}
      cookieConsent
      contentStyles={{ mb: 0 }}
      header={<Header template="Makler" headerStyles={{ marginTop: 0 }} />}
    >
      <Helmet>
        <meta name="content-entity" content="page" />
        <meta name="content-id" content={page.contentful_id} />
      </Helmet>
      <SEO
        entity={{ ...page }}
        title={page.metaTitle || page.headline}
        canonical={page.canonical}
        type="page"
        description={page.metaDescription}
        indexable={page.indexable}
        breadcrumbs={breadcrumbs}
      />

      {/* Spacer for the fixed nav so content doesn't float beneath it */}
      <Box h={{ base: '69.42px', md: '82px' }} aria-hidden />

      {/* ACTION ROW (anchor-scroll links + primary CTA) — sticky below the nav */}
      <Box
        position="sticky"
        top={{ base: '69.42px', md: '82px' }}
        zIndex={20}
        bg="rgba(255,255,255,.9)"
        backdropFilter="blur(14px)"
        borderBottom="1px solid"
        borderColor={LINE}
      >
        <Wrap>
          <Flex
            as="nav"
            align="center"
            justify={{ base: 'center', md: 'flex-end' }}
            gap={{ base: '14px', md: '26px' }}
            minH="74px"
            py={{ base: '12px', md: '0' }}
            wrap="wrap"
          >
            {[
              ['Objektbörse', '#boerse'],
              ['Autopilot', '#autopilot'],
              ['Preise', '#preise'],
              ['Fragen', '#faq'],
            ].map(([label, href]) => (
              <Box
                key={href}
                as="a"
                href={href}
                color={INK}
                fontSize="16px"
                fontWeight={600}
                _hover={{ color: 'blue.400' }}
              >
                {label}
              </Box>
            ))}
            <Cta source="Nav: Kostenlos starten">Kostenlos starten</Cta>
          </Flex>
        </Wrap>
      </Box>

      {/* HERO */}
      <Box
        as="header"
        textAlign="center"
        py={{ base: '56px', md: '80px' }}
        background="radial-gradient(900px 500px at 50% -10%, #EAF4FE, #fff 70%)"
      >
        <Wrap>
          <Heading
            as="h1"
            maxW="14em"
            mx="auto"
            color={INK}
            fontSize={{ base: '42px', md: '58px', lg: '72px' }}
            lineHeight="1.08"
            letterSpacing="-0.02em"
          >
            Akquise, die läuft, während Sie{' '}
            <Box as="span" color="blue.400">
              verkaufen.
            </Box>
          </Heading>
          <Text
            fontSize={{ base: '18px', md: '20px' }}
            color={MUTED}
            maxW="33em"
            mx="auto"
            mt="22px"
            mb="30px"
          >
            Die Objektbörse bringt die Eigentümer, der Autopilot macht die
            Termine. Sie übernehmen, wo Ihr Können zählt.
          </Text>
          <Flex gap="12px" justify="center" wrap="wrap">
            <Cta large source="Hero: Kostenlos starten">
              Kostenlos starten
            </Cta>
          </Flex>
        </Wrap>
      </Box>

      {/* TRUST */}
      <Box pt="8px" pb="40px">
        <Wrap>
          <Flex
            align="center"
            justify="center"
            gap={{ base: '28px', md: '56px' }}
            wrap="wrap"
          >
            {[
              ['seit 2014', 'am Markt'],
              ['3.000+', 'Makler vertrauen uns'],
              ['6 Mio.+', 'exklusive KI-Trainingsdaten'],
            ].map(([big, small]) => (
              <Box key={small} textAlign="center">
                <Text
                  as="b"
                  display="block"
                  fontSize="34px"
                  color={INK}
                  lineHeight="1"
                  mb="0"
                >
                  {big}
                </Text>
                <Text
                  as="span"
                  display="block"
                  fontSize="14px"
                  color={MUTED}
                  mt="6px"
                  mb="0"
                >
                  {small}
                </Text>
              </Box>
            ))}
          </Flex>
        </Wrap>
      </Box>

      {/* LOGOS (marquee) */}
      <Box
        borderTop="1px solid"
        borderColor="rgba(46,45,56,.06)"
        overflow="hidden"
      >
        <Text
          textAlign="center"
          fontSize="12px"
          letterSpacing="0.14em"
          textTransform="uppercase"
          color={MUTED}
          pt="26px"
          pb="20px"
          mb="0"
        >
          Makler aus ganz Deutschland akquirieren mit HAUSGOLD
        </Text>
        <Flex
          gap="64px"
          align="center"
          opacity={0.55}
          w="max-content"
          pb="12px"
          animation={`${marquee} 42s linear infinite`}
        >
          {[...MAKLER_LOGOS, ...MAKLER_LOGOS].map((logo, i) => (
            <Image
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              src={logo.src}
              alt={logo.alt}
              h="40px"
              w="auto"
              maxW="140px"
              objectFit="contain"
              flexShrink={0}
            />
          ))}
        </Flex>
      </Box>

      {/* OBJEKTBÖRSE */}
      <Box as="section" id="boerse" py={{ base: '60px', md: '88px' }}>
        <Wrap>
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: '32px', md: '48px' }}
            alignItems="center"
            bg="#F2F6FB"
            borderRadius="26px"
            p={{ base: '32px', md: '48px' }}
            boxShadow={SHADOW_SM}
          >
            <Box>
              <Flex align="center" gap="10px" mb="16px" wrap="wrap">
                <Box
                  bg="white"
                  color="#C6434A"
                  px="13px"
                  py="6px"
                  borderRadius="7px"
                  fontSize="12px"
                  fontWeight={700}
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                >
                  Objektbörse
                </Box>
                <Text as="span" fontSize="11.5px" color={MUTED} mb="0">
                  · auch einzeln nutzbar
                </Text>
              </Flex>
              <Heading
                as="h2"
                color={INK}
                fontSize={{ base: '30px', md: '40px' }}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                mb="14px"
              >
                Verkaufsbereite Eigentümer aus vielen Quellen, an einer Stelle.
              </Heading>
              <Flex
                align="center"
                gap="16px"
                borderLeft="3px solid"
                borderColor="red.400"
                pl="18px"
                my="20px"
              >
                <Text
                  as="b"
                  fontSize="40px"
                  color="red.400"
                  lineHeight="1"
                  letterSpacing="-0.03em"
                  flex="none"
                  mb="0"
                >
                  28.800 €
                </Text>
                <Text
                  as="span"
                  fontSize="15px"
                  color={MUTED}
                  lineHeight="1.4"
                  mb="0"
                >
                  mehr Umsatz im Jahr, im Schnitt je aktivem Makler.
                </Text>
              </Flex>
              <FeatList
                items={[
                  [
                    'Freie Objektwahl.',
                    'Keine Fixkosten, keine Mindestabnahme.',
                  ],
                  [
                    'Telefonisch geprüft,',
                    'mit Qualitätsgarantie auf viele Objekte.',
                  ],
                  [
                    'Intelligentes Matching.',
                    'Sie bekommen, was zu Ihnen passt.',
                  ],
                ]}
              />
              <Cta colorScheme="blue" source="Objektbörse: Region prüfen">
                Region prüfen
              </Cta>
            </Box>
            <ObjectCard />
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* AUTOPILOT */}
      <Box as="section" id="autopilot" py={{ base: '60px', md: '64px' }}>
        <Wrap>
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: '32px', md: '48px' }}
            alignItems="center"
            bg="blue.200"
            borderRadius="26px"
            p={{ base: '32px', md: '48px' }}
            boxShadow={SHADOW_SM}
          >
            <Box>
              <Flex align="center" gap="10px" mb="16px" wrap="wrap">
                <Box
                  bg="white"
                  color="blue.600"
                  px="13px"
                  py="6px"
                  borderRadius="7px"
                  fontSize="12px"
                  fontWeight={700}
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                >
                  Autopilot
                </Box>
                <Text as="span" fontSize="12px" color={MUTED} mb="0">
                  · auch einzeln nutzbar
                </Text>
              </Flex>
              <Heading
                as="h2"
                color={INK}
                fontSize={{ base: '30px', md: '40px' }}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                mb="14px"
              >
                Holen Sie mehr aus Ihren Kontakten, ganz ohne Ihr Zutun.
              </Heading>
              <Flex
                align="center"
                gap="16px"
                borderLeft="3px solid"
                borderColor="blue.400"
                pl="18px"
                my="20px"
              >
                <Text
                  as="b"
                  fontSize="40px"
                  color="blue.400"
                  lineHeight="1"
                  letterSpacing="-0.03em"
                  flex="none"
                  mb="0"
                >
                  +34 %
                </Text>
                <Text
                  as="span"
                  fontSize="15px"
                  color={MUTED}
                  lineHeight="1.4"
                  mb="0"
                >
                  mehr Aufträge gewinnen Makler mit dem Autopiloten, im Schnitt.
                  Weil er aus über 6 Millionen Akquisedaten gelernt hat.
                </Text>
              </Flex>
              <FeatList
                items={[
                  [
                    'Erreicht 9 von 10 Eigentümern.',
                    'Zur richtigen Zeit, über Anruf, SMS oder E-Mail.',
                  ],
                  [
                    'Erkennt, wer wirklich verkaufen will,',
                    'und vereinbart den Termin für Sie.',
                  ],
                  [
                    'Übergibt Ihnen den fertigen Termin.',
                    'Sie übernehmen erst, wenn es persönlich wird.',
                  ],
                ]}
              />
              <Text fontSize="15px" color={MUTED} mt="-2px" mb="22px">
                <Text as="b" color={INK}>
                  Aus allen Ihren Quellen.
                </Text>{' '}
                Per CSV, automatisch direkt aus der HAUSGOLD Börse und von
                ImmoScout24, laufend mehr.
              </Text>
              <Cta colorScheme="blue" source="Autopilot: Autopilot testen">
                Autopilot testen
              </Cta>
            </Box>
            <CallCard />
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* SO LÄUFT ES (Teil des Autopiloten) */}
      <Box as="section" py={{ base: '60px', md: '64px' }}>
        <Wrap>
          <Box
            maxW="1000px"
            mx="auto"
            borderLeft="3px solid"
            borderColor="blue.600"
            pl={{ base: '20px', md: '44px' }}
          >
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing="48px"
              alignItems="center"
            >
              <Box>
                <Belongs>Teil des Autopiloten</Belongs>
                <Kicker>So läuft es</Kicker>
                <Heading
                  as="h2"
                  color={INK}
                  fontSize={{ base: '30px', md: '40px' }}
                  lineHeight="1.1"
                  letterSpacing="-0.02em"
                  pt="24px"
                  mb="14px"
                >
                  Der Autopilot arbeitet. Sie behalten die Kontrolle.
                </Heading>
                <Text fontSize="19px" color={MUTED} mt="14px" mb="24px">
                  Er läuft im Hintergrund und meldet sich nur, wenn Sie wirklich
                  gebraucht werden.
                </Text>
                <FeatList
                  mb="0"
                  items={[
                    [
                      'Läuft von allein.',
                      'Termine landen direkt in Ihrem Kalender, Sie werden nur geholt, wenn ein Eigentümer persönlich mit Ihnen sprechen möchte.',
                    ],
                    [
                      'Eingreifen jederzeit.',
                      'Einen Kontakt selbst übernehmen, pausieren oder ganz stoppen.',
                    ],
                    [
                      'Kennt Ihre Grenze.',
                      'Ist Ihre Kapazität erreicht, macht er keine neuen Termine.',
                    ],
                  ]}
                />
              </Box>
              <CockpitCard />
            </SimpleGrid>
          </Box>
        </Wrap>
      </Box>

      {/* REAKTIVIERUNG (Teil des Autopiloten) */}
      <Box as="section" py={{ base: '60px', md: '64px' }}>
        <Wrap>
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: '26px', md: '44px' }}
            alignItems="center"
            bg="blue.200"
            borderRadius="26px"
            p={{ base: '32px', md: '48px' }}
            boxShadow={SHADOW_SM}
          >
            <Box>
              <Belongs>Teil des Autopiloten</Belongs>
              <Kicker>Reaktivierung</Kicker>
              <Heading
                as="h2"
                color={INK}
                fontSize={{ base: '30px', md: '40px' }}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                pt="24px"
                mb="14px"
              >
                Ihr Bestand ist voller Aufträge, die niemand mehr anruft.
              </Heading>
              <Text fontSize="18px" color={MUTED} mb="22px">
                Der Autopilot arbeitet Ihre bestehenden Kontakte durch und
                meldet sich, sobald einer wieder verkaufen will. Ohne neuen
                Einkauf, ohne zusätzliche Marketingkosten.
              </Text>
              <Cta
                colorScheme="blue"
                source="Reaktivierung: Bestand prüfen lassen"
              >
                Bestand prüfen lassen
              </Cta>
            </Box>
            <Box
              bg="white"
              borderRadius="16px"
              p="34px"
              textAlign="center"
              boxShadow={SHADOW_SM}
            >
              <Text
                as="span"
                display="block"
                fontSize="58px"
                fontWeight={700}
                color="blue.400"
                lineHeight="1"
                mb="0"
              >
                82.500 €
              </Text>
              <Text fontSize="15px" color={MUTED} mt="10px" mb="0">
                durchschnittlicher Einkaufswert der ungenutzten Kontakte in
                einer Maklerdatenbank.
              </Text>
            </Box>
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* STIMMEN */}
      <Box as="section" py={{ base: '60px', md: '88px' }}>
        <Wrap>
          <Box textAlign="center" mb="48px">
            <Kicker>Aus der Praxis</Kicker>
            <Heading
              as="h2"
              color={INK}
              fontSize={{ base: '30px', md: '40px' }}
              letterSpacing="-0.02em"
              pt="24px"
            >
              Was Makler sagen
            </Heading>
          </Box>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="22px">
            {TESTIMONIALS.map(({ photo, name, meta, quote, link }) => (
              <Link
                key={name}
                to={link}
                display="block"
                border="1px solid"
                borderColor={LINE}
                borderRadius="16px"
                overflow="hidden"
                bg="white"
                boxShadow={SHADOW_SM}
                transition="box-shadow .2s ease, transform .2s ease"
                _hover={{
                  textDecoration: 'none',
                  boxShadow: SHADOW,
                  transform: 'translateY(-2px)',
                }}
              >
                <Box position="relative">
                  <AspectRatio ratio={4 / 5}>
                    <Image
                      src={photo}
                      alt={name}
                      objectFit="cover"
                      w="100%"
                      h="100%"
                    />
                  </AspectRatio>
                  <Text
                    position="absolute"
                    left="18px"
                    right="18px"
                    bottom="18px"
                    fontWeight={700}
                    fontSize="18px"
                    lineHeight="1.3"
                    color="white"
                    textShadow="0 2px 14px rgba(0,0,0,.6)"
                    mb="0"
                  >
                    „{quote}“
                  </Text>
                </Box>
                <Box px="18px" py="16px">
                  <Text
                    as="b"
                    display="block"
                    fontSize="16px"
                    color={INK}
                    mb="0"
                  >
                    {name}
                  </Text>
                  <Text as="span" fontSize="13px" color={MUTED} mb="0">
                    {meta}
                  </Text>
                </Box>
              </Link>
            ))}
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* PREISE */}
      <Box as="section" id="preise" py={{ base: '60px', md: '88px' }}>
        <Wrap>
          <Box textAlign="center" maxW="38em" mx="auto" mb="48px">
            <Kicker>Preise</Kicker>
            <Heading
              as="h2"
              color={INK}
              fontSize={{ base: '30px', md: '40px' }}
              letterSpacing="-0.02em"
              pt="24px"
            >
              Preise, die sich für Sie rechnen.
            </Heading>
            <Text fontSize="19px" color={MUTED} mt="14px" mb="0">
              Zwei Produkte, einzeln oder zusammen. Sie starten ohne Risiko und
              zahlen für das, was Sie wirklich brauchen.
            </Text>
          </Box>
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing="24px"
            alignItems="stretch"
          >
            {/* Objektbörse */}
            <Flex
              direction="column"
              position="relative"
              border="1px solid"
              borderColor={LINE}
              borderTop="4px solid"
              borderTopColor="red.400"
              borderRadius="20px"
              p={{ base: '32px', md: '38px 34px' }}
              bg="white"
              boxShadow={SHADOW_SM}
            >
              <Text
                as="span"
                fontSize="12px"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="red.400"
                mb="16px"
              >
                Objektbörse
              </Text>
              <Text
                as="span"
                display="block"
                fontWeight={700}
                fontSize="30px"
                color={INK}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                mb="6px"
              >
                Zahlung je Kontakt
              </Text>
              <Text fontSize="14px" color={MUTED} mb="0">
                Sie entscheiden bei jedem Kontakt selbst
              </Text>
              <PriceFeatures
                color="red.400"
                tint="#FEEEEF"
                items={[
                  'Geprüfte, verkaufsbereite Eigentümer',
                  'Keine Fixkosten, keine Mindestabnahme',
                  'Gemeinschaftsgeschäft: kleine Gebühr vorab, Rest nach dem Verkauf. Oder Festpreis mit voller Provision.',
                ]}
              />
              <Cta
                colorScheme="blue"
                variant="outline"
                width="100%"
                justifyContent="center"
                mt="auto"
                source="Preise Objektbörse: Zugang sichern"
              >
                Zugang sichern
              </Cta>
            </Flex>

            {/* Autopilot (highlight) */}
            <Flex
              direction="column"
              position="relative"
              border="1px solid"
              borderColor="rgba(16,152,243,.35)"
              borderRadius="20px"
              p={{ base: '32px', md: '38px 34px' }}
              background="linear-gradient(180deg, #EAF4FE 0%, #fff 58%)"
              boxShadow={SHADOW}
            >
              <Box
                position="absolute"
                top="-13px"
                right="28px"
                bg="blue.400"
                color="white"
                fontSize="11px"
                letterSpacing="0.1em"
                textTransform="uppercase"
                px="14px"
                py="6px"
                borderRadius="100px"
                boxShadow={SHADOW_SM}
              >
                Testphase gratis
              </Box>
              <Text
                as="span"
                fontSize="12px"
                letterSpacing="0.14em"
                textTransform="uppercase"
                color="blue.400"
                mb="16px"
              >
                Autopilot
              </Text>
              <Text
                as="span"
                display="block"
                fontWeight={700}
                fontSize="30px"
                color={INK}
                lineHeight="1.1"
                letterSpacing="-0.02em"
                mb="6px"
              >
                Kostenlos starten
              </Text>
              <Text fontSize="14px" color={MUTED} mb="0">
                Danach ein fester Monatspreis nach Umfang
              </Text>
              <PriceFeatures
                color="blue.400"
                tint="#fff"
                boxShadow="inset 0 0 0 1px rgba(16,152,243,.35)"
                items={[
                  'Erreicht, qualifiziert und terminiert für Sie',
                  'Wächst mit Ihrem Volumen, jederzeit erweiterbar',
                  'Fester Monatspreis, egal wie lange er für Ihr Wunschergebnis braucht',
                ]}
              />
              <Cta
                colorScheme="blue"
                width="100%"
                justifyContent="center"
                mt="auto"
                source="Preise Autopilot: Kostenlos testen"
              >
                Kostenlos testen
              </Cta>
            </Flex>
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* START */}
      <Box as="section" id="start" py={{ base: '60px', md: '88px' }}>
        <Wrap>
          <Box textAlign="center" maxW="30em" mx="auto" mb="48px">
            <Kicker>Der Start</Kicker>
            <Heading
              as="h2"
              color={INK}
              fontSize={{ base: '30px', md: '40px' }}
              letterSpacing="-0.02em"
              pt="24px"
            >
              In einer Stunde startklar, nicht in Wochen.
            </Heading>
            <Text fontSize="19px" color={MUTED} mt="14px" mb="0">
              Anmelden dauert Minuten, freigeschaltet sind Sie meist innerhalb
              einer Stunde. Mit der Börse, dem Autopiloten oder beidem.
            </Text>
          </Box>
          <SimpleGrid
            columns={{ base: 1, md: 3 }}
            spacing="0"
            border="1px solid"
            borderColor={LINE}
            borderRadius="16px"
            overflow="hidden"
            bg="white"
            boxShadow={SHADOW_SM}
          >
            {TIMELINE.map(({ step, title, text }, i) => (
              <Box
                key={step}
                p="30px"
                borderRight={{
                  md: i < TIMELINE.length - 1 ? '1px solid' : undefined,
                }}
                borderBottom={{
                  base: i < TIMELINE.length - 1 ? '1px solid' : undefined,
                  md: 'none',
                }}
                borderColor={LINE}
              >
                <Text
                  as="span"
                  display="block"
                  fontSize="12px"
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  color="blue.400"
                  mb="12px"
                >
                  {step}
                </Text>
                <Heading as="h4" fontSize="20px" color={INK} pt="0" mb="8px">
                  {title}
                </Heading>
                <Text fontSize="15px" color={MUTED} mb="0">
                  {text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
          <Box textAlign="center" mt="36px">
            <Cta large source="Der Start: Kostenlos starten">
              Kostenlos starten
            </Cta>
          </Box>
        </Wrap>
      </Box>

      {/* SICHERHEIT (dark) */}
      <Box
        as="section"
        bg={INK}
        color="white"
        py={{ base: '60px', md: '88px' }}
      >
        <Wrap>
          <Box maxW="30em" mb="44px">
            <Kicker color="#8FC7F7">Sicherheit</Kicker>
            <Heading
              as="h2"
              color="white"
              fontSize={{ base: '30px', md: '40px' }}
              letterSpacing="-0.02em"
              pt="24px"
            >
              Ihre Daten sind sicher.
            </Heading>
          </Box>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="20px">
            {SECURITY.map(({ icon, title, text }) => (
              <Box
                key={title}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="12px"
                p="24px"
              >
                <Flex
                  w="38px"
                  h="38px"
                  align="center"
                  justify="center"
                  mb="16px"
                >
                  <Image src={icon} alt="" boxSize="38px" />
                </Flex>
                <Text
                  as="b"
                  display="block"
                  fontSize="16px"
                  color="white"
                  mb="6px"
                >
                  {title}
                </Text>
                <Text as="span" fontSize="14.5px" color="whiteAlpha.700" mb="0">
                  {text}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Wrap>
      </Box>

      {/* FAQ */}
      <Box as="section" id="faq" py={{ base: '60px', md: '64px' }}>
        <Wrap>
          <Box textAlign="center" mb="48px">
            <Kicker>Fragen</Kicker>
            <Heading
              as="h2"
              color={INK}
              fontSize={{ base: '30px', md: '40px' }}
              letterSpacing="-0.02em"
              pt="24px"
            >
              Häufig gefragt
            </Heading>
          </Box>
          <Box maxW="780px" mx="auto">
            {FAQ.map(({ group, items }, i) => (
              <Box key={group}>
                <Text
                  fontSize="11.5px"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                  color="blue.400"
                  mt={i === 0 ? 0 : '30px'}
                  mb="2px"
                >
                  {group}
                </Text>
                {items.map(({ q, a }) => (
                  <Box
                    key={q}
                    as="details"
                    borderBottom="1px solid"
                    borderColor={LINE}
                    py="20px"
                    sx={{
                      '& > summary': {
                        fontWeight: 700,
                        fontSize: '18px',
                        color: INK,
                        cursor: 'pointer',
                        listStyle: 'none',
                        display: 'flex',
                        gap: '14px',
                      },
                      '& > summary::-webkit-details-marker': {
                        display: 'none',
                      },
                      '& > summary::before': {
                        content: '"+"',
                        color: 'blue.400',
                        fontSize: '22px',
                        fontWeight: 400,
                        width: '18px',
                        flex: 'none',
                        lineHeight: '1.1',
                      },
                      '&[open] > summary::before': { content: '"–"' },
                    }}
                  >
                    <Box as="summary">{q}</Box>
                    <Text
                      mt="10px"
                      ml="32px"
                      mb="0"
                      fontSize="16px"
                      color={MUTED}
                    >
                      {a}
                    </Text>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Wrap>
      </Box>

      {/* ABSCHLUSS */}
      <Box
        as="section"
        textAlign="center"
        color="white"
        py={{ base: '64px', md: '88px' }}
        position="relative"
        overflow="hidden"
        background="linear-gradient(135deg, #1098F3 0%, #1180D8 60%, #0B6FC4 100%)"
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(520px 300px at 82% 120%, rgba(244,99,100,.45), transparent 70%)',
          },
        }}
      >
        <Wrap position="relative" zIndex={2}>
          <Heading
            as="h2"
            color="white"
            maxW="16em"
            mx="auto"
            mb="14px"
            fontSize={{ base: '30px', md: '40px' }}
            letterSpacing="-0.02em"
          >
            Ihre Akquise läuft. Sie verkaufen.
          </Heading>
          <Text
            color="whiteAlpha.900"
            mx="auto"
            mb="28px"
            fontSize={{ base: '18px', md: '19px' }}
          >
            Kostenlos testen, kein Risiko, jederzeit kündbar.
          </Text>
          <Flex gap="12px" justify="center" wrap="wrap">
            <Cta
              large
              colorScheme="blue"
              bg="white"
              color="blue.600"
              _hover={{ bg: '#EAF4FE' }}
              source="Abschluss: Kostenlos starten"
            >
              Kostenlos starten
            </Cta>
            <Button
              as="a"
              href="tel:+4940226161423"
              colorScheme="blue"
              bg="blue.400"
              color="white"
              fontWeight={700}
              borderRadius="10px"
              height="auto"
              fontSize="17px"
              px="34px"
              py="17px"
              _hover={{ bg: 'blue.500' }}
            >
              040 22 6161 423
            </Button>
          </Flex>
        </Wrap>
      </Box>

      {exitIntent && (
        <ExitIntentModal
          show={showPopup}
          image={exitIntent.image}
          text={exitIntent.text.childMarkdownRemark.html}
          button={exitIntent.button}
          setShowPopup={setShowPopup}
        />
      )}
    </Layout>
  );
};

// Feature list used inside the two pricing cards.
function PriceFeatures({ items, color, tint, boxShadow }) {
  return (
    <Flex
      as="ul"
      direction="column"
      gap="13px"
      listStyleType="none"
      m="0"
      mt="24px"
      mb="28px"
      pt="24px"
      borderTop="1px solid"
      borderColor="rgba(46,45,56,.06)"
      p="24px 0 0"
    >
      {items.map((item) => (
        <Flex as="li" key={item} align="flex-start" gap="11px">
          <Flex
            flex="none"
            w="19px"
            h="19px"
            mt="1px"
            borderRadius="50%"
            bg={tint}
            color={color}
            boxShadow={boxShadow}
            align="center"
            justify="center"
            fontSize="11px"
            fontWeight={700}
          >
            ✓
          </Flex>
          <Text as="span" fontSize="15px" color={INK} lineHeight="1.45" mb="0">
            {item}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

export default Autopilot;

export const pageQuery = graphql`
  query Autopilot {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulPage(slug: { eq: "autopilot" }) {
      useArticleSchema
      contentful_id
      id
      title
      headline
      headlineColor
      subTitle
      indexable
      metaTitle
      metaDescription
      slug
      updatedAt
      createdAt
      childPages {
        id
        slug
        title
      }
      page {
        id
        slug
        title
        childPages {
          id
          slug
          title
        }
      }
      exitIntent {
        image {
          gatsbyImageData(layout: FIXED, width: 170)
          title
        }
        text {
          childMarkdownRemark {
            html
          }
        }
        button {
          title
          url
          color
          size
          shadow
        }
      }
    }
  }
`;
